import { useCallback, useRef, useState } from 'react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';
import { isValidOutcome } from '@/utils/trainers';

import Feedback from '@/components/ui/trainers/shared/Feedback';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import TrainerButton from './TrainerButton';
import ErrorMessage from './shared/ErrorMessage';
import SelectableImageGrid from './SelectableImageGrid';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
  modifyPosition?: boolean;
  modifyAzimuth?: boolean;
}

const ImageQualityTrainer: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const { leaseId, images = [] } = data?.content || {};

  const description =
    initialDescription || (data?.content ? `Which Image Is Better?` : '');

  const isDataReady = Boolean(data?.content);

  const [isBenchmark, setIsBenchmark] = useState(false);

  const { feedback, setFeedback } = useTrainerFeedback();

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };

  const onSubmit = async ({ outcome, value }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }

    let result;

    if (!isValidOutcome(outcome)) {
      result = value;
    }
    const sending = {
      leaseId,
      ...(result && { result }),
      ...(outcome && { outcome }),
    };

    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  return (
    <div className="flex size-full flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <SelectableImageGrid
            images={images}
            width={2}
            height={1}
            selectable={false}
          />
          <TrainerController
            containerRef={controllerRef}
            isDataReady={isDataReady}
            onSubmit={onSubmit}
            data={data}
            tool={tool}>
            <TrainerButtons />
            <TrainerSelect />
            <TrainerInputs />
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default ImageQualityTrainer;
